/**
 * Procore observation type → ACC issue subtype (title) mapping.
 *
 * The ONE Autodesk-specific concern with no Procore equivalent: an ACC issue
 * needs a subtype. LedAI observations already carry a Procore observation type
 * ("Category - Type"); when Autodesk is active we remap that to the closest ACC
 * subtype configured on the project. Resolution to a subtype *id* happens at
 * sync time against the project's live issue-types (title match, case-insensitive),
 * with a fallback to DEFAULT_OBSERVATION_SUBTYPE and a skip-with-warning if even
 * that is missing.
 *
 * ⚠️ Rows marked REVIEW are judgment calls (no clean ACC equivalent) — the ACC
 * subtype set here has no "Environmental"/"Commissioning"/"Warranty" type.
 */

/** ACC subtype titles present in the project (targets of the mapping). */
// Exact titles confirmed from the project's Type dropdown (case-insensitive
// resolution, but kept exact for display). Punch subtypes weren't in the
// screenshots — "Punch List"/"Pre-Punch List" are best-guess casing.
export const ACC_SUBTYPE_TITLES = [
  "Clash",
  "Coordination",
  "General",
  "Observation",
  "Quality",
  "Pre-Punch List",
  "Punch List",
  "Safety",
  "Safety Infraction",
  "Work List",
  "Work to Complete",
] as const;
export type AccSubtypeTitle = (typeof ACC_SUBTYPE_TITLES)[number];

export const DEFAULT_OBSERVATION_SUBTYPE: AccSubtypeTitle = "Observation";

/** Keyed by the Procore observation type label "Category - Type". */
export const PROCORE_OBSERVATION_TYPE_TO_ACC_SUBTYPE: Record<string, AccSubtypeTitle> = {
  // Quality → Quality (Pre-Punch is the clean punch bridge)
  "Quality - Corrective Action": "Quality",
  "Quality - Deficiency": "Quality",
  "Quality - Non-Conformance": "Quality",
  "Quality - Pre-Punch": "Pre-Punch List",

  // Safety → Safety (Violation → the infraction subtype)
  "Safety - Corrective Action": "Safety",
  "Safety - Near Miss": "Safety",
  "Safety - Preventative Action": "Safety",
  "Safety - Safety Hazard": "Safety",
  "Safety - Safety Notice": "Safety",
  "Safety - Safety Violation": "Safety Infraction",

  // Environmental → REVIEW (no ACC Environmental type). Hazards → Safety;
  // stewardship → General.
  "Environmental - Air Quality": "Safety",
  "Environmental - Hazardous Materials": "Safety",
  "Environmental - Noise": "Safety",
  "Environmental - Spill": "Safety",
  "Environmental - Cultural Heritage": "General",
  "Environmental - Storm Water": "General",
  "Environmental - Vegetation": "General",
  "Environmental - Waste Management": "General",
  "Environmental - Wildlife": "General",

  // Commissioning / Warranty / Work to Complete → REVIEW (completion work)
  "Commissioning - Commissioning": "Work to Complete",
  "Warranty - Service Call": "Work to Complete",
  "Work to Complete - Work to Complete": "Work to Complete",
};

/** Punch items map to punch subtypes (default vs pre-punch). */
export const PUNCH_SUBTYPE: AccSubtypeTitle = "Punch List";
export const PRE_PUNCH_SUBTYPE: AccSubtypeTitle = "Pre-Punch List";

/**
 * The ACC subtype titles an OBSERVATION publishes into: this table's targets
 * plus the default. Derived from the table, never a second list.
 */
const OBSERVATION_SUBTYPE_TARGETS: readonly AccSubtypeTitle[] = [
  DEFAULT_OBSERVATION_SUBTYPE,
  ...new Set(Object.values(PROCORE_OBSERVATION_TYPE_TO_ACC_SUBTYPE)),
];

/**
 * Resolve an observation's type → the ACC subtype title to target.
 *
 * The type is normally a Procore observation type label ("Category - Type").
 * ACC Task 5 (Ruling R10): when ACC changes an issue's subtype to another
 * OBSERVATION subtype, the inbound pull stores that ACC subtype title as the
 * observation's type — the table is many-to-one ("Quality - Deficiency" and
 * "Quality - Non-Conformance" both target "Quality"), so no single Procore label
 * can be recovered, and guessing one would invent a type nobody chose. A type
 * that already IS one of this table's own targets therefore publishes as
 * itself, so the round trip is exact. Anything else keeps the default.
 */
export function accSubtypeTitleForObservationType(procoreObservationType: string | undefined): AccSubtypeTitle {
  if (!procoreObservationType) return DEFAULT_OBSERVATION_SUBTYPE;
  const trimmed = procoreObservationType.trim();
  const mapped = PROCORE_OBSERVATION_TYPE_TO_ACC_SUBTYPE[trimmed];
  if (mapped) return mapped;
  const wanted = trimmed.toLowerCase();
  return (
    OBSERVATION_SUBTYPE_TARGETS.find((title) => title.toLowerCase() === wanted) ??
    DEFAULT_OBSERVATION_SUBTYPE
  );
}

/** Resolve a punch item → the ACC subtype title (pre-punch when flagged). */
export function accSubtypeTitleForPunch(isPrePunch = false): AccSubtypeTitle {
  return isPrePunch ? PRE_PUNCH_SUBTYPE : PUNCH_SUBTYPE;
}
